import React from "react";
import { Button } from "../../ui";
import { CampaignAssetAccess } from "../../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUsers } from "@fortawesome/free-solid-svg-icons";

export interface AssetAccessListProps {
  /** List of users with access to the asset */
  accessUsers: CampaignAssetAccess[];
  /** Callback when access is revoked for a character */
  onRevokeAccess?: (_characterId: number) => void;
  /** Whether the list is in edit mode */
  isEditing?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const AssetAccessList: React.FC<AssetAccessListProps> = ({
  accessUsers,
  onRevokeAccess,
  isEditing = false,
  className = "",
}) => {
  if (!accessUsers || accessUsers.length === 0) {
    return (
      <div className={`mt-4 pt-4 border-t border-gray-700 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faUsers} className="text-gray-400" />
          <p className="text-sm font-medium text-gray-400">Character Access</p>
        </div>
        <p className="text-xs text-gray-500">No characters have access to this asset</p>
      </div>
    );
  }

  return (
    <div className={`mt-4 pt-4 border-t border-gray-700 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <FontAwesomeIcon icon={faUsers} className="text-gray-400" />
        <p className="text-sm font-medium text-gray-300">Character Access:</p>
        <span className="bg-blue-900 text-blue-200 px-2 py-1 rounded-full text-xs">
          {accessUsers.length} character{accessUsers.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {accessUsers.map((access) => (
          <div
            key={access.characterId}
            className="bg-blue-900 text-blue-200 px-3 py-2 rounded-full text-sm flex items-center gap-2 transition-colors hover:bg-blue-800"
          >
            <span className="font-medium">{access.character.name}</span>

            {/* Granted date */}
            <span className="text-blue-300 text-xs opacity-75">
              {new Date(access.grantedAt).toLocaleDateString()}
            </span>

            {/* Revoke button */}
            {isEditing && onRevokeAccess && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRevokeAccess(access.characterId)}
                className="text-red-400 hover:text-red-300 p-1 ml-1 -mr-1"
                title={`Revoke access for ${access.character.name}`}
              >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {!isEditing && (
        <p className="text-xs text-gray-500 mt-2">Click "Edit" to modify character access</p>
      )}
    </div>
  );
};

export default AssetAccessList;
